import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Folder,
  Wrench,
  Plus,
  X,
  Send,
  Palmtree,
  Coffee,
  FileText,
  Check,
  XCircle,
  Edit2,
  Trash2
} from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toETInputValue } from '../utils/timezone';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EVENT_TYPE_STYLES = {
  meeting: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', icon: '🤝' },
  project: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', icon: '📋' },
  service: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', icon: '🔧' },
  reminder: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', icon: '⏰' },
  holiday: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: '🏖️' },
  time_off: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300', icon: '☕' },
  other: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', icon: '📌' }
};

const REQUEST_TYPES = [
  { value: 'holiday', label: 'Holiday / Vacation', icon: Palmtree, color: 'text-orange-600' },
  { value: 'time_off', label: 'Time Off / Personal', icon: Coffee, color: 'text-violet-600' },
  { value: 'other', label: 'Other Request', icon: FileText, color: 'text-gray-600' }
];

const MySchedule = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [allEventsData, setAllEventsData] = useState([]); // Store unfiltered for badge counts
  const [myRequestHistory, setMyRequestHistory] = useState([]); // Store ALL user's requests for history
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'requests', 'events'
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseAction, setResponseAction] = useState(''); // 'approve' or 'deny'
  const [responseExplanation, setResponseExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Admin edit/delete state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    start_datetime: '',
    end_datetime: '',
    location: '',
    all_day: false
  });
  
  // Role checks
  const isAdmin = user.role === 'admin' || user.role === 'supervisor';
  const isClient = user.role === 'client';
  
  // Request form state (for employees)
  const [requestForm, setRequestForm] = useState({
    title: '',
    description: '',
    event_type: 'holiday',
    start_datetime: '',
    end_datetime: '',
    all_day: true
  });
  
  // Meeting request form state (for clients)
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    location: ''
  });

  useEffect(() => {
    fetchMyEvents();
  }, [filter, typeFilter]);

  // Fetch user's complete request history (separate from filtered events)
  useEffect(() => {
    if (!isAdmin) {
      fetchMyRequestHistory();
    }
  }, []);

  const fetchMyRequestHistory = async () => {
    try {
      // Fetch ALL events without date filters to get complete request history
      const response = await axios.get(`${API}/calendar/events`);
      const allEvents = response.data;
      // Filter to only user's own requests
      const userRequests = allEvents.filter(e => 
        (e.is_employee_request || e.is_client_request) && 
        e.created_by === user.id
      );
      setMyRequestHistory(userRequests);
    } catch (error) {
      console.error('Error fetching request history:', error);
    }
  };

  const fetchMyEvents = async () => {
    try {
      setLoading(true);
      const now = new Date().toISOString();
      
      let params = {};
      if (filter === 'upcoming') {
        params.start_date = now;
      } else if (filter === 'past') {
        params.end_date = now;
      }
      
      const response = await axios.get(`${API}/calendar/events`, { params });
      
      let allEvents = response.data;
      setAllEventsData(allEvents); // Store unfiltered for badge counts
      
      let filteredEvents = [...allEvents];
      
      // Apply type filter for admin (but keep allEvents for badge counts)
      if (isAdmin && typeFilter !== 'all') {
        if (typeFilter === 'requests') {
          filteredEvents = filteredEvents.filter(e => e.is_employee_request || e.is_client_request);
        } else if (typeFilter === 'events') {
          filteredEvents = filteredEvents.filter(e => !e.is_employee_request && !e.is_client_request);
        }
      }
      
      if (filter === 'past') {
        filteredEvents.sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime));
      } else {
        filteredEvents.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
      }
      
      setEvents(filteredEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate stats and filter counts for admin (based on unfiltered data)
  const allEventsCount = allEventsData.length;
  const pendingRequestsCount = allEventsData.filter(e => 
    (e.is_employee_request || e.is_client_request) && e.request_status === 'pending'
  ).length;
  const regularEventsCount = allEventsData.filter(e => !e.is_employee_request && !e.is_client_request).length;
  const allRequestsCount = allEventsData.filter(e => e.is_employee_request || e.is_client_request).length;
  
  // Get user's own approved/denied/pending requests from complete history (for employees/clients)
  const myApprovedRequests = myRequestHistory.filter(e => e.request_status === 'approved');
  const myDeniedRequests = myRequestHistory.filter(e => e.request_status === 'denied');
  const myPendingRequests = myRequestHistory.filter(e => e.request_status === 'pending');
  
  const stats = {
    totalEvents: allEventsCount,
    pendingRequests: allEventsData.filter(e => (e.is_employee_request || e.is_client_request) && e.request_status === 'pending').length,
    upcomingMeetings: allEventsData.filter(e => e.event_type === 'meeting' && new Date(e.start_datetime) > new Date()).length,
    holidayRequests: allEventsData.filter(e => e.event_type === 'holiday').length,
    timeOffRequests: allEventsData.filter(e => e.event_type === 'time_off').length
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const requestData = {
        ...requestForm,
        start_datetime: new Date(requestForm.start_datetime).toISOString(),
        end_datetime: requestForm.end_datetime ? new Date(requestForm.end_datetime).toISOString() : null
      };
      
      await axios.post(`${API}/calendar/employee-request`, requestData);
      
      // Reset form and close modal
      setRequestForm({
        title: '',
        description: '',
        event_type: 'holiday',
        start_datetime: '',
        end_datetime: '',
        all_day: true
      });
      setShowRequestModal(false);
      
      // Refresh events and request history
      fetchMyEvents();
      fetchMyRequestHistory();
      
      alert('Your request has been submitted! Admin will be notified.');
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitMeetingRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const requestData = {
        title: meetingForm.title,
        description: meetingForm.description,
        event_type: 'meeting',
        start_datetime: new Date(meetingForm.start_datetime).toISOString(),
        end_datetime: meetingForm.end_datetime ? new Date(meetingForm.end_datetime).toISOString() : null,
        location: meetingForm.location,
        all_day: false
      };
      
      await axios.post(`${API}/calendar/client-meeting-request`, requestData);
      
      setMeetingForm({
        title: '',
        description: '',
        start_datetime: '',
        end_datetime: '',
        location: ''
      });
      setShowMeetingModal(false);
      
      fetchMyEvents();
      fetchMyRequestHistory();
      
      alert('Your meeting request has been submitted! You will receive an email once it is reviewed.');
    } catch (error) {
      console.error('Error submitting meeting request:', error);
      alert('Failed to submit meeting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openResponseModal = (event, action) => {
    setSelectedRequest(event);
    setResponseAction(action);
    setResponseExplanation('');
    setShowResponseModal(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      const endpoint = responseAction === 'approve' 
        ? `${API}/calendar/events/${selectedRequest.id}/approve`
        : `${API}/calendar/events/${selectedRequest.id}/deny`;
      
      await axios.post(endpoint, { explanation: responseExplanation || null });
      
      const actionText = responseAction === 'approve' ? 'approved' : 'denied';
      alert(`Request ${actionText}! An email notification has been sent.`);
      
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseExplanation('');
      fetchMyEvents();
    } catch (error) {
      console.error(`Error ${responseAction}ing request:`, error);
      alert(`Failed to ${responseAction} request. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveRequest = (event) => {
    openResponseModal(event, 'approve');
  };

  const handleDenyRequest = (event) => {
    openResponseModal(event, 'deny');
  };

  // Admin Edit Event Handler
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'meeting',
      start_datetime: event.start_datetime ? toETInputValue(event.start_datetime) : '',
      end_datetime: event.end_datetime ? toETInputValue(event.end_datetime) : '',
      location: event.location || '',
      all_day: event.all_day || false
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEvent) return;
    
    setSubmitting(true);
    try {
      const updateData = {
        title: editForm.title,
        description: editForm.description,
        event_type: editForm.event_type,
        start_datetime: new Date(editForm.start_datetime).toISOString(),
        end_datetime: editForm.end_datetime ? new Date(editForm.end_datetime).toISOString() : null,
        location: editForm.location,
        all_day: editForm.all_day
      };
      
      await axios.put(`${API}/calendar/events/${editingEvent.id}`, updateData);
      
      alert('Event updated successfully!');
      setShowEditModal(false);
      setEditingEvent(null);
      fetchMyEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Admin Delete Event Handler
  const handleDeleteEvent = (event) => {
    setEditingEvent(event);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEvent = async () => {
    if (!editingEvent) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/calendar/events/${editingEvent.id}`);
      
      alert('Event deleted successfully!');
      setShowDeleteConfirm(false);
      setEditingEvent(null);
      fetchMyEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { timeZone: 'America/Toronto', hour: '2-digit',
      minute: '2-digit' });
  };

  const getTimeUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) {
      const absDiff = Math.abs(diff);
      const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
      if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
      const hours = Math.floor(absDiff / (1000 * 60 * 60));
      if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      return 'Just ended';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    const minutes = Math.floor(diff / (1000 * 60));
    return minutes > 0 ? `in ${minutes} min` : 'Starting now';
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (dateString) => {
    const date = new Date(dateString);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const date = new Date(event.start_datetime).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  // Check if user can submit requests (employees only, not sub-contractors)
  const canSubmitRequests = user.role === 'employee';

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-red-600" />
              {isAdmin ? 'Schedule Overview' : 'My Schedule'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdmin 
                ? 'Quick overview of all events and employee requests'
                : canSubmitRequests 
                  ? 'View your scheduled events and submit time-off requests'
                  : isClient 
                    ? 'View your scheduled events and request meetings'
                    : 'View your scheduled events'}
            </p>
          </div>
          
          {/* Submit Request Button - Only for employees */}
          {canSubmitRequests && (
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors shadow-sm"
            >
              <Plus className="w-5 h-5" />
              Request Time Off
            </button>
          )}
          
          {/* Request Meeting Button - Only for clients */}
          {isClient && (
            <button
              onClick={() => setShowMeetingModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-5 h-5" />
              Request Meeting
            </button>
          )}
        </div>

        {/* Admin Stats Cards */}
        {isAdmin && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
              <p className="text-sm text-gray-500">Total Events</p>
            </div>
            <div className="bg-white rounded-lg border border-orange-200 p-4 shadow-sm bg-orange-50">
              <p className="text-2xl font-bold text-orange-600">{stats.pendingRequests}</p>
              <p className="text-sm text-orange-600">Pending Requests</p>
            </div>
            <div className="bg-white rounded-lg border border-blue-200 p-4 shadow-sm bg-blue-50">
              <p className="text-2xl font-bold text-blue-600">{stats.upcomingMeetings}</p>
              <p className="text-sm text-blue-600">Upcoming Meetings</p>
            </div>
            <div className="bg-white rounded-lg border border-violet-200 p-4 shadow-sm bg-violet-50">
              <p className="text-2xl font-bold text-violet-600">{stats.holidayRequests + stats.timeOffRequests}</p>
              <p className="text-sm text-violet-600">Time-Off Requests</p>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-4">
          <div className="flex gap-2">
            {[
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'past', label: 'Past' },
              { value: 'all', label: 'All Events' }
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab.value
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Admin Type Filter */}
        {isAdmin && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setTypeFilter('all')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  typeFilter === 'all'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                📋 All
                {allEventsCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                    typeFilter === 'all' ? 'bg-white text-orange-600' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {allEventsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTypeFilter('events')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  typeFilter === 'events'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                📅 Events
                {regularEventsCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                    typeFilter === 'events' ? 'bg-white text-blue-600' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {regularEventsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTypeFilter('requests')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  typeFilter === 'requests'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                🏖️ Requests
                {pendingRequestsCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full animate-pulse ${
                    typeFilter === 'requests' ? 'bg-white text-orange-600' : 'bg-orange-500 text-white'
                  }`}>
                    {pendingRequestsCount}
                  </span>
                )}
                {allRequestsCount > 0 && pendingRequestsCount === 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                    typeFilter === 'requests' ? 'bg-white text-orange-600' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {allRequestsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Events List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Scheduled Events</h3>
            <p className="text-gray-500 mb-4">
              {filter === 'upcoming' 
                ? "You don't have any upcoming events scheduled."
                : filter === 'past'
                ? "You don't have any past events."
                : "No events have been scheduled for you yet."}
            </p>
            {canSubmitRequests && (
              <button
                onClick={() => setShowRequestModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                <Plus className="w-4 h-4" />
                Request Time Off
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => {
              return (
                <div key={dateKey}>
                  {/* Date Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      isToday(dateKey) 
                        ? 'bg-red-100 text-red-700'
                        : isTomorrow(dateKey)
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {isToday(dateKey) ? 'Today' : isTomorrow(dateKey) ? 'Tomorrow' : formatDate(dateKey)}
                    </div>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* Events for this date */}
                  <div className="space-y-3">
                    {dateEvents.map(event => {
                      const eventStyle = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.other;
                      const isPast = new Date(event.start_datetime) < new Date();
                      const isOwnRequest = event.is_employee_request && event.created_by === user.id;
                      
                      return (
                        <div
                          key={event.id}
                          className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden transition-all hover:shadow-md ${
                            isPast ? 'opacity-60' : ''
                          } ${eventStyle.border}`}
                        >
                          <div className="flex">
                            {/* Color bar */}
                            <div 
                              className="w-2 flex-shrink-0"
                              style={{ backgroundColor: event.color || '#dc2626' }}
                            />
                            
                            <div className="flex-1 p-4">
                              {/* Event Header */}
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-xl">{eventStyle.icon}</span>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {event.title}
                                    </h3>
                                    {event.is_employee_request && isAdmin && (
                                      <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                                        Employee Request
                                      </span>
                                    )}
                                    {isOwnRequest && !isAdmin && (
                                      <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                                        Your Request
                                      </span>
                                    )}
                                    {event.request_status && (
                                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                                        event.request_status === 'approved' 
                                          ? 'bg-green-100 text-green-700'
                                          : event.request_status === 'denied'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {event.request_status.charAt(0).toUpperCase() + event.request_status.slice(1)}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Date Range Display - Prominent for requests */}
                                  {event.is_employee_request && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 my-2">
                                      <p className="text-sm font-medium text-amber-800">
                                        📅 {formatDate(event.start_datetime)}
                                        {event.end_datetime && event.end_datetime !== event.start_datetime && (
                                          <> → {formatDate(event.end_datetime)}</>
                                        )}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {event.description && (
                                    <p className="text-gray-600 text-sm mb-3">
                                      {event.description}
                                    </p>
                                  )}
                                </div>
                                
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                                  isPast 
                                    ? 'bg-gray-100 text-gray-500'
                                    : eventStyle.bg + ' ' + eventStyle.text
                                }`}>
                                  {getTimeUntil(event.start_datetime)}
                                </span>
                              </div>

                              {/* Event Details */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Clock className="w-4 h-4 text-gray-400" />
                                  <span>
                                    {event.all_day ? 'All Day' : formatTime(event.start_datetime)}
                                    {event.end_datetime && !event.all_day && ` - ${formatTime(event.end_datetime)}`}
                                  </span>
                                </div>
                                
                                {event.location && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span>{event.location}</span>
                                  </div>
                                )}
                                
                                {event.project_name && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Folder className="w-4 h-4 text-gray-400" />
                                    <span>{event.project_name}</span>
                                  </div>
                                )}
                                
                                {event.service_name && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Wrench className="w-4 h-4 text-gray-400" />
                                    <span>{event.service_name}</span>
                                  </div>
                                )}
                                
                                {/* Show end date for multi-day non-request events */}
                                {!event.is_employee_request && event.end_datetime && 
                                 new Date(event.end_datetime).toDateString() !== new Date(event.start_datetime).toDateString() && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <span>Ends: {formatDate(event.end_datetime)}</span>
                                  </div>
                                )}
                              </div>

                              {/* Organizer - only show if not own request */}
                              {event.created_by_name && !isOwnRequest && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <p className="text-xs text-gray-500">
                                    {event.is_employee_request ? 'Requested by: ' : 'Organized by: '}
                                    <span className="font-medium text-gray-700">{event.created_by_name}</span>
                                  </p>
                                </div>
                              )}
                              
                              {/* Admin Approve/Deny Buttons for pending requests (employee or client) */}
                              {isAdmin && (event.is_employee_request || event.is_client_request) && event.request_status === 'pending' && (
                                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveRequest(event);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                                  >
                                    <Check className="w-4 h-4" />
                                    Approve
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDenyRequest(event);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Deny
                                  </button>
                                </div>
                              )}
                              
                              {/* Show who approved/denied and any explanation */}
                              {(event.is_employee_request || event.is_client_request) && event.request_status !== 'pending' && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <p className="text-xs text-gray-500">
                                    {event.request_status === 'approved' ? (
                                      <span className="text-green-600">✅ Approved by {event.approved_by_name}</span>
                                    ) : (
                                      <span className="text-red-600">❌ Denied by {event.denied_by_name}</span>
                                    )}
                                  </p>
                                  {event.admin_response && (
                                    <p className="text-xs text-gray-600 mt-1 bg-gray-50 p-2 rounded">
                                      <strong>Response:</strong> {event.admin_response}
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Admin Edit/Delete Buttons - Available for ALL events */}
                              {isAdmin && (
                                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditEvent(event);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteEvent(event);
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* My Requests Reference Section - For Employees and Clients */}
        {!isAdmin && (myApprovedRequests.length > 0 || myDeniedRequests.length > 0 || myPendingRequests.length > 0) && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              📋 My Request History
            </h2>
            
            {/* Approved Requests */}
            {myApprovedRequests.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Approved Requests ({myApprovedRequests.length})
                </h3>
                <div className="space-y-3">
                  {myApprovedRequests.map(request => (
                    <div 
                      key={request.id}
                      className="bg-green-50 border-2 border-green-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            {request.event_type === 'holiday' ? '🏖️' : 
                             request.event_type === 'time_off' ? '☕' : 
                             request.event_type === 'meeting' ? '🤝' : '📌'}
                            {request.title.replace(`${user.name} - `, '').replace('Meeting Request: ', '').replace(`${user.name} - `, '')}
                          </h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(request.start_datetime)}
                              {request.end_datetime && new Date(request.end_datetime).toDateString() !== new Date(request.start_datetime).toDateString() && (
                                <> → {formatDate(request.end_datetime)}</>
                              )}
                            </p>
                            {request.description && (
                              <p className="text-gray-500">{request.description}</p>
                            )}
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                          ✓ APPROVED
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-green-200 text-xs text-green-700">
                        <p>✅ Approved by <strong>{request.approved_by_name}</strong></p>
                        {request.approved_at && (
                          <p className="text-green-600">on {new Date(request.approved_at).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        )}
                        {request.admin_response && (
                          <p className="mt-2 bg-white p-2 rounded border border-green-200 text-gray-700">
                            <strong>Message:</strong> {request.admin_response}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Pending Requests */}
            {myPendingRequests.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending Requests ({myPendingRequests.length})
                </h3>
                <div className="space-y-3">
                  {myPendingRequests.map(request => (
                    <div 
                      key={request.id}
                      className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            {request.event_type === 'holiday' ? '🏖️' : 
                             request.event_type === 'time_off' ? '☕' : 
                             request.event_type === 'meeting' ? '🤝' : '📌'}
                            {request.title.replace(`${user.name} - `, '').replace('Meeting Request: ', '').replace(`${user.name} - `, '')}
                          </h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(request.start_datetime)}
                              {request.end_datetime && new Date(request.end_datetime).toDateString() !== new Date(request.start_datetime).toDateString() && (
                                <> → {formatDate(request.end_datetime)}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full animate-pulse">
                          ⏳ PENDING
                        </span>
                      </div>
                      <p className="mt-3 text-xs text-yellow-700">
                        Awaiting admin review...
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Denied Requests */}
            {myDeniedRequests.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Denied Requests ({myDeniedRequests.length})
                </h3>
                <div className="space-y-3">
                  {myDeniedRequests.map(request => (
                    <div 
                      key={request.id}
                      className="bg-red-50 border-2 border-red-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            {request.event_type === 'holiday' ? '🏖️' : 
                             request.event_type === 'time_off' ? '☕' : 
                             request.event_type === 'meeting' ? '🤝' : '📌'}
                            {request.title.replace(`${user.name} - `, '').replace('Meeting Request: ', '').replace(`${user.name} - `, '')}
                          </h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(request.start_datetime)}
                              {request.end_datetime && new Date(request.end_datetime).toDateString() !== new Date(request.end_datetime).toDateString() && (
                                <> → {formatDate(request.end_datetime)}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                          ✗ DENIED
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-red-200 text-xs text-red-700">
                        <p>❌ Denied by <strong>{request.denied_by_name}</strong></p>
                        {request.denied_at && (
                          <p className="text-red-600">on {new Date(request.denied_at).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        )}
                        {request.admin_response && (
                          <p className="mt-2 bg-white p-2 rounded border border-red-200 text-gray-700">
                            <strong>Reason:</strong> {request.admin_response}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Request Time Off Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Palmtree className="w-6 h-6 text-orange-500" />
                Request Time Off
              </h2>
              <button 
                onClick={() => setShowRequestModal(false)} 
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitRequest} className="p-6 space-y-4">
              {/* Request Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Request Type
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {REQUEST_TYPES.map(type => (
                    <label
                      key={type.value}
                      className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        requestForm.event_type === type.value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="event_type"
                        value={type.value}
                        checked={requestForm.event_type === type.value}
                        onChange={(e) => setRequestForm(prev => ({ ...prev, event_type: e.target.value }))}
                        className="sr-only"
                      />
                      <type.icon className={`w-5 h-5 ${type.color}`} />
                      <span className="font-medium text-gray-900">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Title/Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title / Reason *
                </label>
                <input
                  type="text"
                  value={requestForm.title}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Family vacation, Doctor's appointment"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={requestForm.start_datetime}
                    onChange={(e) => setRequestForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={requestForm.end_datetime}
                    onChange={(e) => setRequestForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Any additional information..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Your request will be sent to the admin for approval. You will see it in your schedule once submitted.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Meeting Request Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                🤝 Request a Meeting
              </h2>
              <button 
                onClick={() => setShowMeetingModal(false)} 
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitMeetingRequest} className="p-6 space-y-4">
              {/* Meeting Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Subject *
                </label>
                <input
                  type="text"
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Project discussion, Quote request"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={meetingForm.start_datetime}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    value={meetingForm.end_datetime}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Location
                </label>
                <input
                  type="text"
                  value={meetingForm.location}
                  onChange={(e) => setMeetingForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Office, Phone call, Video call"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Message/Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message / What would you like to discuss?
                </label>
                <textarea
                  value={meetingForm.description}
                  onChange={(e) => setMeetingForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Please describe what you would like to discuss..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Your meeting request will be reviewed by our team. You will receive an email notification once it is approved or if we need to suggest an alternative time.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowMeetingModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Response Modal (Approve/Deny with explanation) */}
      {showResponseModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className={`p-6 border-b border-gray-200 ${responseAction === 'approve' ? 'bg-green-50' : 'bg-red-50'}`}>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {responseAction === 'approve' ? (
                  <>
                    <Check className="w-6 h-6 text-green-600" />
                    Approve Request
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-600" />
                    Deny Request
                  </>
                )}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Request Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900">{selectedRequest.title}</p>
                <p className="text-sm text-gray-600 mt-1">
                  From: {selectedRequest.created_by_name}
                </p>
                <p className="text-sm text-gray-600">
                  Date: {formatDate(selectedRequest.start_datetime)}
                </p>
              </div>

              {/* Explanation field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {responseAction === 'approve' ? 'Message (optional)' : 'Reason for denial (recommended)'}
                </label>
                <textarea
                  value={responseExplanation}
                  onChange={(e) => setResponseExplanation(e.target.value)}
                  placeholder={responseAction === 'approve' 
                    ? 'Add any notes or confirmation details...'
                    : 'Please explain why this request cannot be approved...'}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                    responseAction === 'approve' 
                      ? 'border-green-300 focus:ring-green-500' 
                      : 'border-red-300 focus:ring-red-500'
                  }`}
                />
              </div>

              {/* Info */}
              <p className="text-xs text-gray-500">
                An email notification with your response will be sent to {selectedRequest.created_by_name}.
              </p>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedRequest(null);
                    setResponseExplanation('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitResponse}
                  disabled={submitting}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    responseAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {responseAction === 'approve' ? <Check className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {responseAction === 'approve' ? 'Approve & Send' : 'Deny & Send'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Edit2 className="w-6 h-6 text-blue-600" />
                Edit Event
              </h2>
              <button 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEvent(null);
                }} 
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={editForm.event_type}
                  onChange={(e) => setEditForm(prev => ({ ...prev, event_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="meeting">🤝 Meeting</option>
                  <option value="project">📋 Project</option>
                  <option value="service">🔧 Service</option>
                  <option value="reminder">⏰ Reminder</option>
                  <option value="holiday">🏖️ Holiday</option>
                  <option value="time_off">☕ Time Off</option>
                  <option value="other">📌 Other</option>
                </select>
              </div>

              {/* All Day Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="all_day"
                  checked={editForm.all_day}
                  onChange={(e) => setEditForm(prev => ({ ...prev, all_day: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="all_day" className="text-sm font-medium text-gray-700">
                  All Day Event
                </label>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start {editForm.all_day ? 'Date' : 'Date & Time'} *
                  </label>
                  <input
                    type={editForm.all_day ? 'date' : 'datetime-local'}
                    value={editForm.all_day ? editForm.start_datetime.slice(0, 10) : editForm.start_datetime}
                    onChange={(e) => setEditForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End {editForm.all_day ? 'Date' : 'Date & Time'}
                  </label>
                  <input
                    type={editForm.all_day ? 'date' : 'datetime-local'}
                    value={editForm.end_datetime ? (editForm.all_day ? editForm.end_datetime.slice(0, 10) : editForm.end_datetime) : ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Office, Client Site, Video Call"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Event details..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingEvent(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={submitting || !editForm.title || !editForm.start_datetime}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Delete Confirmation Modal */}
      {showDeleteConfirm && editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 bg-red-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Trash2 className="w-6 h-6 text-red-600" />
                Delete Event
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Event Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900">{editingEvent.title}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(editingEvent.start_datetime)}
                </p>
                {editingEvent.is_employee_request && (
                  <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                    Employee Request
                  </span>
                )}
                {editingEvent.is_client_request && (
                  <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                    Client Request
                  </span>
                )}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> This action cannot be undone. The event will be permanently deleted.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setEditingEvent(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteEvent}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Event
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySchedule;
